import type React from 'react'

import {useRouteHeaderData} from './hooks/use-route-header-data'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {
  condensedResponsiveWrapperClasses,
  fullWidthResponsiveWrapperClasses,
  StickyPullRequestHeader,
} from './components/StickyPullRequestHeader'
import {SplitPageLayout, type SplitPageLayoutHeaderProps} from '@primer/react'
import {LivePullRequestHeader} from './components/PullRequestHeader'
import {ObservableBox as StickyHeaderActivationThreshold} from '@github-ui/use-sticky-header/ObservableBox'
import type {PropsWithChildren} from 'react'
import {useMatch} from 'react-router-dom'
import {FilesRoutePath} from './routes/route-paths'

// AppLayout is an alias for the SlottedAppLayout component
// It exists for improved readability for Commits JSON route + tests
export function AppLayout(props: {children?: React.ReactNode}) {
  return <SlottedAppLayout>{props.children}</SlottedAppLayout>
}

type SlottedLayoutProps = {
  leadingContent?: React.ReactNode
  stickyHeader?: React.ReactNode
  headerDivider?: SplitPageLayoutHeaderProps['divider']
}

/**
 * SlottedLayout is a layout component that allows custom leading content + sticky headers to be passed in as props.
 * This component lets us compose the layout in the route instead of inheriting from the base layout.
 * Any use of this component assumes that you're on the Files route.
 * This is necessary because the Files route needs to pass in custom content to those areas of the layout.
 * This layout component is NOT compatible for use with Data Router.
 * The use case for nested routes providing custom layout content is on React Platform's roadmap.
 */
export function SlottedAppLayout({
  children,
  headerDivider = 'none',
  leadingContent,
  stickyHeader,
}: PropsWithChildren<SlottedLayoutProps>) {
  const {aliveChannel, pullRequest, bannersData, repository, urls, user} = useRouteHeaderData()
  const {isSticky, observe, unobserve} = useStickyHeader()

  const isFilesRoute = useMatch(`${FilesRoutePath}/*`) !== null
  const responsiveWrapperClasses = isFilesRoute ? fullWidthResponsiveWrapperClasses : condensedResponsiveWrapperClasses

  return (
    <PageDataContextProvider basePageDataUrl={urls.conversation}>
      {isSticky &&
        (stickyHeader ?? (
          <StickyPullRequestHeader
            repository={repository}
            pullRequest={pullRequest}
            responsiveWrapperClasses={responsiveWrapperClasses}
          />
        ))}
      <div className={`mt-4 ${responsiveWrapperClasses}`}>
        <SplitPageLayout>
          <SplitPageLayout.Header divider={headerDivider} padding="none">
            <LivePullRequestHeader
              aliveChannel={aliveChannel}
              bannersData={bannersData}
              isFilesRoute={isFilesRoute}
              repository={repository}
              pullRequest={pullRequest}
              urls={urls}
              user={user}
            />
            <StickyHeaderActivationThreshold
              sx={{visibility: 'hidden', height: '1px'}}
              onObserve={observe}
              onUnobserve={unobserve}
            />

            {/* this isnt necessarily header content, but we need to pass it into the header to
            render content across the page on top of the pane + content
            this is a workaround to a limitation of PageLayout */}
            {leadingContent}
          </SplitPageLayout.Header>
          {children}
        </SplitPageLayout>
      </div>
    </PageDataContextProvider>
  )
}

try{ AppLayout.displayName ||= 'AppLayout' } catch {}
try{ SlottedAppLayout.displayName ||= 'SlottedAppLayout' } catch {}